import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Link,
  Checkbox,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { googleMapsAPI, leadGenAPI, linkedinAPI } from "../services/api";
import toast from "react-hot-toast";

// LinkedIn location IDs mapping
const LINKEDIN_LOCATION_IDS = {
  Mexico: 103323778,
  Argentina: 100446943,
  Bolivia: 104379274,
  Brazil: 106057199,
  Colombia: 100876405,
  Ecuador: 106373116,
  Guyana: 105836293,
  Paraguay: 104065273,
  Peru: 102927786,
  Suriname: 105530931,
  Uruguay: 100867946,
  Venezuela: 101490751,
  Canada: 101174742,
  "United States": 103644278,
  Belize: 105912732,
  "Costa Rica": 101739942,
  "El Salvador": 106522560,
  Guatemala: 100877388,
  Honduras: 101937718,
  Nicaragua: 105517145,
  Panama: 100808673,
  Cuba: 106429766,
  Haiti: 100993490,
  "Dominican Republic": 105057336,
};

// Get country names from location IDs string (e.g., "[103644278]" or array)
const getCountryNamesFromLocationIds = (locationIds) => {
  if (!locationIds) return "";

  let idsArray = [];
  if (typeof locationIds === "string") {
    try {
      // Try to parse as JSON array string like "[103644278]"
      idsArray = JSON.parse(locationIds);
    } catch (e) {
      // If not JSON, try splitting by comma
      idsArray = locationIds
        .split(",")
        .map((id) => id.trim().replace(/[\[\]]/g, ""));
    }
  } else if (Array.isArray(locationIds)) {
    idsArray = locationIds;
  }

  const countryNames = idsArray
    .map((id) => {
      const entry = Object.entries(LINKEDIN_LOCATION_IDS).find(
        ([_, locationId]) => locationId.toString() === id.toString()
      );
      return entry ? entry[0] : null;
    })
    .filter((name) => name !== null);

  return countryNames.join(", ") || "";
};

const BatchHistoryDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");
  const source = searchParams.get("source");
  const searchName = searchParams.get("search_name");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [expandedBusinesses, setExpandedBusinesses] = useState(new Set());
  const [selectedDecisionMakers, setSelectedDecisionMakers] = useState([]);
  const [isShortlisting, setIsShortlisting] = useState(false);

  useEffect(() => {
    if (jobId && source === "googlemaps") {
      fetchJobsHistory();
    } else if (jobId && source === "linkedin") {
      fetchLinkedinJobsHistory();
    } else if (!jobId) {
      setError("Job ID is required");
      toast.error("Job ID is required");
      navigate("/lead-history");
    } else {
      setError("Invalid source. Please use Google Maps or LinkedIn.");
      toast.error("Invalid source. Please use Google Maps or LinkedIn.");
      navigate("/lead-history");
    }
  }, [jobId, source]);

  const fetchJobsHistory = async () => {
    if (!jobId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await googleMapsAPI.getJobsHistory(jobId);
      setHistoryData(response);
      toast.success("Batch history loaded successfully");
    } catch (error) {
      console.error("Error fetching jobs history:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load batch history";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLinkedinJobsHistory = async () => {
    if (!jobId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use apolloPeopleSearch API as requested
      const searchNameParam = searchName || "Tech Lead"; // Default fallback
      const response = await linkedinAPI.apolloPeopleSearch(
        jobId,
        searchNameParam
      );

      // Transform LinkedIn response to match the expected format
      // LinkedIn response structure: { success, job_id, buisinesses, search_response }
      // buisinesses can contain both company objects and person objects
      const businessesArray = response.buisinesses || response.businesses || [];

      // Separate companies from people in buisinesses array
      // Companies have company_id, company_name, etc.
      // People have id (string), name, title, etc.
      const companies = businessesArray.filter(
        (b) => b.company_id || b.company_name || typeof b.id === "number"
      );
      const peopleFromBusinesses = businessesArray.filter(
        (b) =>
          !b.company_id && !b.company_name && typeof b.id === "string" && b.name
      );

      const transformedData = {
        job_id: response.job_id || jobId,
        businesses: {
          linkedin: companies,
        },
        enriched_persons: [],
      };

      // Extract enriched persons - prioritize enriched_data for LinkedIn
      const allPersons = [];

      // 1. From enriched_data (primary source for LinkedIn)
      if (response.enriched_data && Array.isArray(response.enriched_data)) {
        response.enriched_data.forEach((person) => {
          // Map enriched_data structure to our format
          allPersons.push({
            serial_id: person.id || `person_${allPersons.length}`,
            apollo_id: person.id,
            id: person.id,
            person_id: person.id,
            first_name: person.first_name || "",
            last_name: person.last_name || "",
            name:
              person.name ||
              `${person.first_name || ""} ${person.last_name || ""}`.trim(),
            title: person.title || "",
            email: person.email || null,
            linkedin_url: person.linkedin_url || null,
            photo_url: person.photo_url || null,
            headline: person.headline || person.title || "",
            organization_id:
              person.organization?.id || person.organization_id || null,
            organization_name:
              person.organization?.name || person.organization_name || "",
            city: person.city || "",
            state: person.state || "",
            country: person.country || "",
            postal_code: person.postal_code || "",
            formatted_address: person.formatted_address || "",
            seniority: person.seniority || "",
            email_status: person.email_status || "",
            // Keep all original data
            ...person,
          });
        });
      }

      // 2. From search_response.people (fallback if enriched_data not available)
      if (
        allPersons.length === 0 &&
        response.search_response?.people &&
        Array.isArray(response.search_response.people)
      ) {
        response.search_response.people.forEach((person) => {
          allPersons.push({
            serial_id: person.id || `person_${allPersons.length}`,
            apollo_id: person.id,
            id: person.id,
            person_id: person.id,
            first_name: person.first_name || "",
            last_name: person.last_name_obfuscated || person.last_name || "",
            name: person.organization?.name || "",
            title: person.title || "",
            email: person.email || null,
            linkedin_url: person.linkedin_url || null,
            photo_url: person.photo_url || null,
            headline: person.headline || person.title || "",
            organization_id: person.organization?.id || null,
            organization_name: person.organization?.name || "",
            ...person,
          });
        });
      }

      // 3. From people found in buisinesses array (fallback)
      if (allPersons.length === 0) {
        peopleFromBusinesses.forEach((person) => {
          allPersons.push({
            serial_id: person.id || `person_${allPersons.length}`,
            apollo_id: person.id,
            id: person.id,
            person_id: person.id,
            first_name: person.first_name || "",
            last_name: person.last_name || "",
            name: person.name || person.organization?.name || "",
            title: person.title || "",
            email: person.email || null,
            linkedin_url: person.linkedin_url || null,
            photo_url: person.photo_url || null,
            headline: person.headline || person.title || "",
            organization_id:
              person.organization?.id || person.organization_id || null,
            organization_name:
              person.organization?.name || person.organization_name || "",
            ...person,
          });
        });
      }

      transformedData.enriched_persons = allPersons;

      setHistoryData(transformedData);
      toast.success("LinkedIn batch history loaded successfully");
    } catch (error) {
      console.error("Error fetching LinkedIn jobs history:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load LinkedIn batch history";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/lead-history");
  };

  // Get decision makers for a specific business based on enriched_persons_serial_ids
  const getDecisionMakersForBusiness = (business) => {
    if (!historyData?.enriched_persons) {
      return [];
    }

    // For LinkedIn, match by organization name with company name
    if (source === "linkedin") {
      const companyName = business.company_name || "";
      if (!companyName) {
        return [];
      }

      return historyData.enriched_persons.filter((person) => {
        // Match person's organization name with company name
        const personOrgName =
          person.organization_name || person.organization?.name || "";

        // Case-insensitive comparison
        return (
          personOrgName.toLowerCase().trim() ===
          companyName.toLowerCase().trim()
        );
      });
    } else {
      // For Google Maps, match by enriched_persons_serial_ids
      if (!business?.enriched_persons_serial_ids) {
        return [];
      }

      const serialIds = Array.isArray(business.enriched_persons_serial_ids)
        ? business.enriched_persons_serial_ids
        : [];

      // Match by serial_id
      return historyData.enriched_persons.filter((person) => {
        return serialIds.includes(person.serial_id);
      });
    }
  };

  // Get all decision makers across all businesses
  const getAllDecisionMakers = () => {
    if (!historyData?.enriched_persons) {
      return [];
    }
    return historyData.enriched_persons;
  };

  // Toggle decision maker selection
  const handleDecisionMakerToggle = (person) => {
    setSelectedDecisionMakers((prev) => {
      const personId =
        person.apollo_id || person.id || person.person_id || person.serial_id;
      const isSelected = prev.some(
        (p) => (p.apollo_id || p.id || p.person_id || p.serial_id) === personId
      );
      if (isSelected) {
        return prev.filter(
          (p) =>
            (p.apollo_id || p.id || p.person_id || p.serial_id) !== personId
        );
      } else {
        return [...prev, person];
      }
    });
  };

  // Check if decision maker is selected
  const isDecisionMakerSelected = (person) => {
    const personId =
      person.apollo_id || person.id || person.person_id || person.serial_id;
    return selectedDecisionMakers.some(
      (p) => (p.apollo_id || p.id || p.person_id || p.serial_id) === personId
    );
  };

  // Handle shortlist decision makers
  const handleShortlistDecisionMakers = async () => {
    if (selectedDecisionMakers.length === 0) {
      toast.error("No decision makers selected to shortlist");
      return;
    }

    setIsShortlisting(true);
    try {
      // Extract Apollo IDs from selected decision makers
      const apolloIds = selectedDecisionMakers
        .map((dm) => dm.apollo_id || dm.id || dm.person_id)
        .filter((id) => id); // Remove any null/undefined values

      if (apolloIds.length === 0) {
        toast.error("No valid Apollo IDs found in selected decision makers");
        setIsShortlisting(false);
        return;
      }

      await leadGenAPI.shortlistDecisionMakers(apolloIds);

      toast.success(
        `Successfully shortlisted ${apolloIds.length} decision maker(s)!`
      );

      // Clear selection after successful shortlisting
      setSelectedDecisionMakers([]);
    } catch (error) {
      console.error("Error shortlisting decision makers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to shortlist decision makers. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsShortlisting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        padding: "24px",
        backgroundColor: "#F9FAFB",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              color: "#364153",
              "&:hover": {
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            Back to Lead History
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Batch History Details
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* History Data */}
      {!isLoading && !error && historyData && (
        <Box>
          {/* Summary Card */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Batch Summary
            </Typography>
            <Grid container spacing={2}>
              {(historyData.businesses?.google_maps ||
                historyData.businesses?.linkedin) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Companies
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {
                      (
                        historyData.businesses?.google_maps ||
                        historyData.businesses?.linkedin ||
                        []
                      ).length
                    }
                  </Typography>
                </Grid>
              )}
              {historyData.enriched_persons && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Decision Makers
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {historyData.enriched_persons.length}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Card>

          {/* Companies Table with Accordion */}
          {((historyData.businesses?.google_maps &&
            historyData.businesses.google_maps.length > 0) ||
            (historyData.businesses?.linkedin &&
              historyData.businesses.linkedin.length > 0)) && (
            <>
              {/* Shortlist Button */}
              {selectedDecisionMakers.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<BookmarkIcon />}
                    onClick={handleShortlistDecisionMakers}
                    disabled={isShortlisting}
                    sx={{
                      backgroundColor: "#10B981",
                      "&:hover": {
                        backgroundColor: "#059669",
                      },
                      textTransform: "none",
                      borderRadius: "8px",
                    }}
                  >
                    {isShortlisting
                      ? "Shortlisting..."
                      : `Add to Shortlisted (${selectedDecisionMakers.length})`}
                  </Button>
                </Box>
              )}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: "1px solid #E5E7EB" }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableCell
                        sx={{ width: "40px", fontWeight: 600 }}
                      ></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Company Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Business Type
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Website</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {source === "linkedin" ? "Industry" : "Rating"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reviews</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Decision Makers
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(
                      historyData.businesses?.google_maps ||
                      historyData.businesses?.linkedin ||
                      []
                    ).map((business, index) => {
                      const decisionMakers =
                        getDecisionMakersForBusiness(business);
                      const businessKey = business.id || `business_${index}`;
                      const isExpanded = expandedBusinesses.has(businessKey);

                      return (
                        <React.Fragment key={businessKey}>
                          {/* Company Row */}
                          <TableRow
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#F9FAFB",
                              },
                              "&:last-child td": {
                                borderBottom: isExpanded
                                  ? "none"
                                  : "1px solid #E5E7EB",
                              },
                            }}
                            onClick={() => {
                              const newExpanded = new Set(expandedBusinesses);
                              if (isExpanded) {
                                newExpanded.delete(businessKey);
                              } else {
                                newExpanded.add(businessKey);
                              }
                              setExpandedBusinesses(newExpanded);
                            }}
                          >
                            <TableCell>
                              {isExpanded ? (
                                <KeyboardArrowDownIcon
                                  sx={{ color: "#6B7280" }}
                                />
                              ) : (
                                <KeyboardArrowRightIcon
                                  sx={{ color: "#6B7280" }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CorporateFareIcon
                                  sx={{ color: "#3B82F6", fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {business.company_name ||
                                    business.name ||
                                    business.owner_name ||
                                    "Unknown Company"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {business.industry ||
                                  business.industry_names ||
                                  business.business_type ||
                                  "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {business.phone_number ||
                                  business.phone ||
                                  "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {business.website_url ||
                              business.website ||
                              business.regular_company_url ? (
                                <Link
                                  href={
                                    business.website_url ||
                                    business.website ||
                                    business.regular_company_url
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: "#3B82F6",
                                    textDecoration: "none",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {business.website_url ||
                                    business.website ||
                                    business.regular_company_url}
                                </Link>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: "200px" }}
                              >
                                {business.full_address ||
                                  business.street_address ||
                                  business.address ||
                                  (business.location_ids
                                    ? getCountryNamesFromLocationIds(
                                        business.location_ids
                                      )
                                    : "") ||
                                  "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {source === "linkedin" &&
                              (business.industry || business.industry_names) ? (
                                <Chip
                                  label={
                                    business.industry || business.industry_names
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: "#DBEAFE",
                                    color: "#1E40AF",
                                  }}
                                />
                              ) : business.rating ? (
                                <Chip
                                  label={`⭐ ${business.rating}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#FEF3C7",
                                    color: "#92400E",
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {business.review_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{ fontWeight: 600 }}
                              >
                                {decisionMakers.length}
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Expanded Decision Makers Row */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell
                                colSpan={10}
                                sx={{ py: 3, backgroundColor: "#F9FAFB" }}
                              >
                                {decisionMakers.length > 0 ? (
                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ mb: 2, fontWeight: 600 }}
                                    >
                                      Decision Makers ({decisionMakers.length})
                                    </Typography>
                                    <TableContainer
                                      component={Paper}
                                      elevation={0}
                                    >
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow
                                            sx={{
                                              backgroundColor: "#FFFFFF",
                                            }}
                                          >
                                            <TableCell
                                              sx={{
                                                fontWeight: 600,
                                                width: "50px",
                                              }}
                                            >
                                              Select
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              Title
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              Email
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              LinkedIn
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              Headline
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                              Organization
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {decisionMakers.map(
                                            (person, personIndex) => (
                                              <TableRow
                                                key={
                                                  person.serial_id ||
                                                  personIndex
                                                }
                                                sx={{
                                                  backgroundColor:
                                                    isDecisionMakerSelected(
                                                      person
                                                    )
                                                      ? "#EFF6FF"
                                                      : "transparent",
                                                  "&:hover": {
                                                    backgroundColor:
                                                      isDecisionMakerSelected(
                                                        person
                                                      )
                                                        ? "#EFF6FF"
                                                        : "#F9FAFB",
                                                  },
                                                }}
                                              >
                                                <TableCell>
                                                  <Checkbox
                                                    checked={isDecisionMakerSelected(
                                                      person
                                                    )}
                                                    onChange={() =>
                                                      handleDecisionMakerToggle(
                                                        person
                                                      )
                                                    }
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    sx={{
                                                      color: "#3B82F6",
                                                      "&.Mui-checked": {
                                                        color: "#3B82F6",
                                                      },
                                                    }}
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                    }}
                                                  >
                                                    {person.photo_url ? (
                                                      <Avatar
                                                        src={person.photo_url}
                                                        sx={{
                                                          width: 32,
                                                          height: 32,
                                                        }}
                                                      />
                                                    ) : (
                                                      <Avatar
                                                        sx={{
                                                          width: 32,
                                                          height: 32,
                                                          bgcolor: "#D1D5DB",
                                                        }}
                                                      >
                                                        <PersonIcon
                                                          sx={{
                                                            fontSize: 18,
                                                          }}
                                                        />
                                                      </Avatar>
                                                    )}
                                                    <Typography variant="body2">
                                                      {`${
                                                        person.first_name || ""
                                                      } ${
                                                        person.last_name || ""
                                                      }`.trim() || "Unknown"}
                                                    </Typography>
                                                  </Box>
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="body2">
                                                    {person.title || "N/A"}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell>
                                                  {person.email ? (
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                      }}
                                                    >
                                                      <EmailIcon
                                                        sx={{
                                                          fontSize: 16,
                                                          color: "#6B7280",
                                                        }}
                                                      />
                                                      <Typography variant="body2">
                                                        {person.email}
                                                      </Typography>
                                                    </Box>
                                                  ) : (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      No email
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {person.linkedin_url ? (
                                                    <Link
                                                      href={person.linkedin_url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                      }}
                                                    >
                                                      <LinkedInIcon
                                                        sx={{
                                                          fontSize: 18,
                                                          color: "#0A66C2",
                                                        }}
                                                      />
                                                      <Typography variant="body2">
                                                        View
                                                      </Typography>
                                                    </Link>
                                                  ) : (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      N/A
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      maxWidth: "300px",
                                                    }}
                                                  >
                                                    {person.headline || "N/A"}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="body2">
                                                    {person.name ||
                                                      person.organization_id ||
                                                      "N/A"}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            )
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                ) : (
                                  <Box sx={{ textAlign: "center", py: 2 }}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No decision makers found for this company
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Empty State */}
          {(!historyData.businesses?.google_maps ||
            historyData.businesses.google_maps.length === 0) &&
            (!historyData.businesses?.linkedin ||
              historyData.businesses.linkedin.length === 0) && (
              <Card sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary">
                  No companies available for this batch
                </Typography>
              </Card>
            )}
        </Box>
      )}
    </Box>
  );
};

export default BatchHistoryDetails;
